import type { AuditEntityType } from '@hemmafixa/shared';
import { Pagination } from '@hemmafixa/ui';
import { useState } from 'react';
import { fill, useI18n } from '@/i18n/context';
import { QueryView } from '../shared/query-view';
import { useAuditLog } from './api';
import { auditChanges, useAuditAction } from './audit-format';

const PAGE_SIZE = 10;

/** The audit entries about one account, provider or job, newest first, for its admin page. */
export function AuditList({ entityType, entityId }: { entityType: AuditEntityType; entityId: string }) {
  const { t, dateTime } = useI18n();
  const [page, setPage] = useState(1);
  const query = useAuditLog({ entityType, entityId, page, pageSize: PAGE_SIZE });
  const actionLabel = useAuditAction();
  const a = t.admin.audit;

  return (
    <QueryView query={query}>
      {(result) =>
        result.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{a.empty}</p>
        ) : (
          <div className="grid gap-4">
            <ol className="grid gap-3 text-sm">
              {result.items.map((entry) => (
                <li key={entry.id} className="grid gap-0.5">
                  <p className="font-medium">{actionLabel(entry.action)}</p>
                  <p className="text-muted-foreground">
                    {entry.actor?.name ?? a.system} · {dateTime(entry.createdAt)}
                  </p>
                  {auditChanges(entry).map((line) => (
                    <p key={line} className="break-words text-muted-foreground">
                      {line}
                    </p>
                  ))}
                </li>
              ))}
            </ol>
            {result.total > PAGE_SIZE && (
              <Pagination
                page={result.page}
                pageSize={result.pageSize}
                total={result.total}
                onPageChange={setPage}
                labels={{
                  previous: t.common.previous,
                  next: t.common.next,
                  showing: (from, to, total) => fill(t.common.showingRange, { from, to, total }),
                }}
              />
            )}
          </div>
        )
      }
    </QueryView>
  );
}
